import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { UpdateLeadForm } from '@tinct/core-api-models';
import { ApiError, tinctApi } from '../../api';

export interface StartLandingPageGenerationParams {
  id: string;
  /**
   * Optional lead-form config, persisted before generation starts so the
   * first generated version renders the form. Omitted/null leaves any
   * stored config untouched.
   */
  leadForm?: UpdateLeadForm | null;
}

export const startLandingPageGeneration = async (
  params: StartLandingPageGenerationParams,
): Promise<void> => {
  return await tinctApi.campaigns.startLandingPageGeneration(
    params.id,
    params.leadForm,
  );
};

/**
 * Create-or-regenerate the campaign's hosted page and start generation (202 —
 * the run continues in background, poll `campaign.landingPage.status`).
 * A 409 means: origin ≠ NEW, context unconfirmed, no brand source site, or a
 * run already in flight — the last one is usually fine to swallow.
 */
export const useStartLandingPageGeneration = (
  options?: Omit<
    UseMutationOptions<void, ApiError, StartLandingPageGenerationParams>,
    'mutationFn'
  >,
) => {
  const { onSuccess, ...restOptions } = options || {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: startLandingPageGeneration,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['campaign', args[1].id] });
      onSuccess?.(...args);
    },
    ...restOptions,
  });
};
